/**
 * Lógica pura de la pestaña Cuentas («Carta náutica»): tipo y sección de cada
 * cuenta, saldos a fin de mes a partir de las sumas mensuales de movimientos,
 * patrimonio neto por mes y reparto del dinero.
 */
import * as monthUtils from '@actual-app/core/shared/months';

export type TipoCuenta = 'corriente' | 'ahorro' | 'inversion' | 'prestamo';
export const TIPOS_CUENTA: readonly TipoCuenta[] = [
  'corriente',
  'ahorro',
  'inversion',
  'prestamo',
];

export type SeccionCuenta = 'presupuesto' | 'fuera' | 'deudas';

type CuentaMinima = { id: string; name: string; offbudget: 0 | 1 };

/** Tipos elegidos al crear la cuenta (pref sincronizada `cuentas-tipos`). */
export function leerTipos(
  raw: string | null | undefined,
): Record<string, TipoCuenta> {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const res: Record<string, TipoCuenta> = {};
    for (const [id, tipo] of Object.entries(parsed)) {
      if (TIPOS_CUENTA.includes(tipo as TipoCuenta)) {
        res[id] = tipo as TipoCuenta;
      }
    }
    return res;
  } catch {
    return {};
  }
}

export function escribirTipo(
  raw: string | null | undefined,
  id: string,
  tipo: TipoCuenta,
): string {
  return JSON.stringify({ ...leerTipos(raw), [id]: tipo });
}

const PARECE_PRESTAMO =
  /hipoteca|mortgage|pr[eé]stamo|\bloan\b|cr[eé]dito|deuda|financiaci[oó]n/i;

// Una tarjeta de crédito no es un préstamo (no tiene TIN ni cuota fija):
// «Tarjeta de crédito» no cae en Deudas por llevar «crédito» en el nombre.
const PARECE_TARJETA = /tarjeta|targeta|\bcard\b|\bvisa\b|mastercard|amex/i;

const PARECE_INVERSION =
  /invers|broker|trade ?republic|indexa|myinvestor|degiro|fondo|etf|bolsa|acciones|cripto|crypto|binance|plan de pensi/i;

/**
 * Tipo de una cuenta: el préstamo manda (línea `#prestamo` en la nota);
 * después, el elegido al crearla; si no, en el presupuesto es corriente y
 * fuera es ahorro (o préstamo o inversión si el nombre lo dice; una tarjeta
 * es corriente).
 */
export function tipoDeCuenta(
  cuenta: CuentaMinima,
  esPrestamo: boolean,
  tipos: Readonly<Record<string, TipoCuenta>>,
): TipoCuenta {
  if (esPrestamo) {
    return 'prestamo';
  }
  const elegido = tipos[cuenta.id];
  if (elegido) {
    return elegido;
  }
  if (!cuenta.offbudget) {
    return 'corriente';
  }
  if (PARECE_TARJETA.test(cuenta.name)) {
    return 'corriente';
  }
  if (PARECE_PRESTAMO.test(cuenta.name)) {
    return 'prestamo';
  }
  return PARECE_INVERSION.test(cuenta.name) ? 'inversion' : 'ahorro';
}

export function seccionDe(
  cuenta: CuentaMinima,
  tipo: TipoCuenta,
): SeccionCuenta {
  if (tipo === 'prestamo') {
    return 'deudas';
  }
  return cuenta.offbudget ? 'fuera' : 'presupuesto';
}

/** Suma de movimientos de una cuenta en un mes (`YYYY-MM`), en céntimos. */
export type SumaMensual = { account: string; month: string; amount: number };

/** Los `n` meses que acaban en `hasta` (incluido), del más antiguo al último. */
export function mesesHasta(hasta: string, n: number): string[] {
  return Array.from({ length: n }, (_, i) =>
    monthUtils.subMonths(hasta, n - 1 - i),
  );
}

/** Primer mes con movimientos (o `hasta` si no hay ninguno). */
export function primerMes(filas: readonly SumaMensual[], hasta: string) {
  let min = hasta;
  for (const f of filas) {
    if (f.month < min) {
      min = f.month;
    }
  }
  return min;
}

/**
 * Saldo de cada cuenta al final de cada mes de `meses` (ordenados): suma
 * acumulada de todo lo anterior. Una pasada por cuenta.
 */
export function saldosFinDeMes(
  filas: readonly SumaMensual[],
  meses: readonly string[],
): Map<string, number[]> {
  const porCuenta = new Map<string, SumaMensual[]>();
  for (const f of filas) {
    const lista = porCuenta.get(f.account);
    if (lista) {
      lista.push(f);
    } else {
      porCuenta.set(f.account, [f]);
    }
  }
  const res = new Map<string, number[]>();
  for (const [cuenta, lista] of porCuenta) {
    lista.sort((a, b) => a.month.localeCompare(b.month));
    const saldos: number[] = [];
    let acumulado = 0;
    let j = 0;
    for (const mes of meses) {
      while (j < lista.length && lista[j].month <= mes) {
        acumulado += lista[j].amount;
        j++;
      }
      saldos.push(acumulado);
    }
    res.set(cuenta, saldos);
  }
  return res;
}

/** Suma, mes a mes, los saldos de las cuentas dadas. */
export function patrimonioPorMes(
  saldos: ReadonlyMap<string, readonly number[]>,
  ids: Iterable<string>,
  numMeses: number,
): number[] {
  const total = new Array<number>(numMeses).fill(0);
  for (const id of ids) {
    const s = saldos.get(id);
    if (!s) {
      continue;
    }
    for (let i = 0; i < numMeses; i++) {
      total[i] += s[i] ?? 0;
    }
  }
  return total;
}

export type Reparto = {
  presupuesto: number;
  ahorro: number;
  inversion: number;
  /** Deudas, en positivo. */
  deudas: number;
};

/**
 * ¿Dónde está mi dinero? Lo positivo de cada cuenta por tramo (en el
 * presupuesto, ahorro, inversión) y las deudas aparte, en positivo.
 */
export function repartir(
  cuentas: readonly {
    tipo: TipoCuenta;
    seccion: SeccionCuenta;
    saldo: number;
  }[],
): Reparto {
  const r: Reparto = { presupuesto: 0, ahorro: 0, inversion: 0, deudas: 0 };
  for (const c of cuentas) {
    if (c.seccion === 'deudas') {
      r.deudas += Math.max(0, -c.saldo);
    } else if (c.saldo > 0) {
      if (c.seccion === 'presupuesto') {
        r.presupuesto += c.saldo;
      } else if (c.tipo === 'inversion') {
        r.inversion += c.saldo;
      } else {
        r.ahorro += c.saldo;
      }
    } else if (c.saldo < 0) {
      // Una corriente en negativo también es deuda.
      r.deudas += -c.saldo;
    }
  }
  return r;
}

/**
 * Cuota mensual (céntimos) de un préstamo francés: capital en céntimos,
 * interés anual en % y plazo en meses.
 */
export function cuotaFrancesa(
  capital: number,
  interesAnual: number,
  meses: number,
): number {
  if (meses <= 0) {
    return 0;
  }
  const i = interesAnual / 100 / 12;
  if (i === 0) {
    return Math.round(capital / meses);
  }
  return Math.round((capital * i) / (1 - Math.pow(1 + i, -meses)));
}

/**
 * ¿Le faltan al préstamo el interés o la cuota (B7 · H56)? Sin ellos no hay
 * amortización ni «% pagado» que valga: se piden antes de enseñar progreso.
 */
export function faltanDatosPrestamo(
  datos:
    | { interes_anual?: number | null; cuota_minima?: number | null }
    | null
    | undefined,
): boolean {
  return datos?.interes_anual == null || datos?.cuota_minima == null;
}

/** Grupo de la leyenda de patrimonio (B7 · H57). */
export type GrupoPatrimonio = 'corriente' | 'ahorro' | 'inversion' | 'deudas';

/** Grupo de una cuenta: en negativo es deuda; un préstamo en positivo, corriente. */
export function grupoPatrimonioDe(c: {
  tipo: TipoCuenta;
  saldo: number;
}): GrupoPatrimonio {
  return c.saldo < 0 ? 'deudas' : c.tipo === 'prestamo' ? 'corriente' : c.tipo;
}

/**
 * «¿Dónde está mi dinero?» por tipo (B7 · H57): lo positivo de cada cuenta
 * suma en su tipo (Corriente, Ahorro, Inversión) y lo negativo en Deudas,
 * en positivo. Sin los grupos vacíos; de mayor a menor (Deudas, al final).
 * El desglose por cuenta queda en la lista.
 */
export function gruposPatrimonio(
  cuentas: readonly { tipo: TipoCuenta; saldo: number }[],
): { grupo: GrupoPatrimonio; valor: number; cuentas: number }[] {
  const suma = new Map<GrupoPatrimonio, { valor: number; cuentas: number }>();
  for (const c of cuentas) {
    if (c.saldo === 0) {
      continue;
    }
    const grupo = grupoPatrimonioDe(c);
    const g = suma.get(grupo) ?? { valor: 0, cuentas: 0 };
    g.valor += Math.abs(c.saldo);
    g.cuentas += 1;
    suma.set(grupo, g);
  }
  const activos = [...suma.entries()]
    .filter(([grupo]) => grupo !== 'deudas')
    .sort((a, b) => b[1].valor - a[1].valor);
  const deudas = suma.get('deudas');
  return [
    ...activos.map(([grupo, g]) => ({ grupo, ...g })),
    ...(deudas ? [{ grupo: 'deudas' as const, ...deudas }] : []),
  ];
}
